import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import AuthContext from "connections/auth/AuthProvider.js";
import {
  assembleChainVisualizationConfig,
  assembleChainVisualizationData,
} from "../../../viewServices/assemblers/ChainVisualizationAssembler";
import {
  extractAvailableAnchors,
  updateChainDataWithRelativity,
} from "../../../viewServices/assemblers/ChainVisualizationRelativityCalculator";
import BackgroundGrid from "./grids/BackgroundGrid";
import ArrowGrid from "./grids/ArrowGrid";
import ContentGrid from "./grids/ContentGrid";
import { getChainVisualizationData } from "../../../viewServices/calls/apiCalls";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  contentArea: {
    position: "relative",
    width: "100%",
    flex: 1,
    overflow: "hidden",
  },
  backgroundLayer: {
    width: "100%",
    height: "100%",
    overflow: "hidden", // No scrollbar needed - overlay handles scrolling
  },
  backgroundLayerContent: {
    visibility: "visible", // Make content visible
    height: "100%",
    pointerEvents: "auto", // Enable clicks on controls
  },
  scrollableOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: "10px",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    pointerEvents: "auto",
    zIndex: 1, // Ensure scrollable content is behind fixed header (which has zIndex: 10)
    // Force scrollbar to overlay content instead of taking space
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0, 0, 0, 0.3)",
      borderRadius: "4px",
      "&:hover": {
        background: "rgba(0, 0, 0, 0.5)",
      },
    },
  },
}));

const ChainVisualization = ({
  data,
  isSkeletonMode = false,
  scenarioId = null,
  filters = {},
  selectedVolume = "volume_sellthrough", // Prop from parent - direct backend value
  selectedRoutes = "", // Prop from parent - lifted to BoardTab
}) => {
  const classes = useStyles();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;

  // State for controls
  const [selectedAnchor, setSelectedAnchor] = useState("");
  const [displayModes, setDisplayModes] = useState([
    "margin",
    "taxes",
    "priceNet",
  ]);
  const [sellinDisplayModes, setSellinDisplayModes] = useState([
    "priceNet",
    "discount",
  ]);
  const [showPriceList, setShowPriceList] = useState(true);
  const [processedData, setProcessedData] = useState(null);
  const [realData, setRealData] = useState(null);
  const [error, setError] = useState(null);

  // Cache fetched chain data to avoid refetching on remount
  const cachedChainData = useRef({});

  // Compute loading state dynamically instead of using useState to avoid timing issues
  const isLoading = useMemo(() => {
    // If no scenario or skeleton mode, not loading
    if (!scenarioId || isSkeletonMode) {
      return false;
    }

    // If we have a scenario but no real data yet, we should be loading
    if (scenarioId && !realData) {
      return true;
    }

    return false;
  }, [scenarioId, isSkeletonMode, realData]);

  // Log loading state changes for debugging
  useEffect(() => {
    console.log("🔄 Loading state changed:", isLoading, {
      scenarioId,
      isSkeletonMode,
      hasRealData: !!realData,
    });
  }, [isLoading, scenarioId, isSkeletonMode, realData]);

  // Fetch real data when scenarioId is provided
  useEffect(() => {
    // Use a ref to track if we're already fetching to prevent duplicate calls
    let isCancelled = false;

    const fetchChainData = async () => {
      if (!scenarioId || isSkeletonMode) {
        // Clear all state when no scenario or skeleton mode
        setRealData(null);
        setProcessedData(null);
        setSelectedAnchor("");
        setError(null);
        return;
      }

      // Create cache key from scenario, filters, volume, and routes
      const cacheKey = JSON.stringify({
        scenarioId,
        filters,
        selectedVolume,
        selectedRoutes,
      });

      // Check cache first
      if (cachedChainData.current[cacheKey]) {
        console.log(
          "✅ Using cached chain visualization data for scenario:",
          scenarioId,
        );
        const cached = cachedChainData.current[cacheKey];
        setRealData(cached);
        setError(null);
        return;
      }

      // Clear previous state when starting new fetch
      setRealData(null);
      setProcessedData(null);
      setSelectedAnchor("");
      setError(null);

      try {
        // selectedVolume is already the direct backend value (volume_sellthrough, volume_sellin, or "1")
        console.log(
          "🔗 Fetching chain visualization data for scenario:",
          scenarioId,
          "with filters:",
          filters,
          "weight field:",
          selectedVolume,
          "considered routes:",
          selectedRoutes,
        );
        const response = await getChainVisualizationData(
          scenarioId,
          filters,
          selectedVolume, // Pass directly - no mapping needed
          selectedRoutes,
        );

        // Check if component was unmounted or effect was cancelled
        if (isCancelled) {
          console.log(
            "🚫 Fetch cancelled - component unmounted or effect changed",
          );
          return;
        }

        console.log("📦 Full API response:", response);

        if (response && response.status === "success" && response.data) {
          console.log("✅ Chain visualization data received:", response.data);
          console.log(
            "📊 Topology subsections:",
            response.data.topologySubsections?.length || 0,
          );

          // Process the response data to ensure compatibility with frontend structure
          const processedData = assembleChainVisualizationData(response.data);
          console.log("🔄 Processed chain visualization data:", processedData);

          // Cache the result
          cachedChainData.current[cacheKey] = processedData;

          setRealData(processedData);
        } else {
          console.warn(
            "⚠️ No chain visualization data received. Response:",
            response,
          );
          setRealData(null);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("❌ Failed to fetch chain visualization data:", err);
          console.error("❌ Error details:", err.response?.data || err.message);
          setError(err);
          setRealData(null);
        }
      }
    };

    fetchChainData();

    // Cleanup function to cancel the effect
    return () => {
      isCancelled = true;
    };
  }, [scenarioId, filters, isSkeletonMode, selectedVolume, selectedRoutes]);

  // Memoize skeleton mock data to prevent recreation on every render
  const skeletonMockData = useMemo(
    () => ({
      priceList: null,
      sections: ["", "", ""], // Empty section labels (removed margins section)
      gridConfig: {
        maxBoxesPerStep: 1,
        baseRowHeight: 40,
        gapBetweenSubsections: 20,
      },
      topologySubsections: [
        {
          name: "", // Empty subsection name
          tracks: [
            {
              id: "distribuidor_comum",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step2" },
                { type: "step3" },
                // { type: "step4" }, // REMOVED - Margins column eliminated
                { type: "step5", selloutPrice: 100 }, // Add selloutPrice for relativity calculations
              ],
            },
            {
              id: "distribuidor_atacadista",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step2" },
                { type: "step3" },
                // { type: "step4" }, // REMOVED - Margins column eliminated
                { type: "step5", selloutPrice: 95 }, // Add selloutPrice for relativity calculations
              ],
            },
            {
              id: "distribuidor_misto",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step2" },
                // Skip step3 like some real tracks
                { type: "step5", selloutPrice: 90 }, // Sellout matches last channel step (step2)
              ],
            },
          ],
        },
        {
          name: "", // Empty subsection name
          tracks: [
            {
              id: "on_trade",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step2" },
                // Skip step3 like some real tracks
                { type: "step5", selloutPrice: 110 }, // Sellout matches last channel step (step2)
              ],
            },
            {
              id: "cc",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step2" },
                // Skip step3 like some real tracks
                { type: "step5", selloutPrice: 105 }, // Sellout matches last channel step (step2)
              ],
            },
            {
              id: "ecommerce",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step2" },
                // Skip step3 like some real tracks
                { type: "step5", selloutPrice: 85 }, // Sellout matches last channel step (step2)
              ],
            },
            {
              id: "key_account",
              label: "", // Empty track label
              steps: [
                { type: "step1" },
                { type: "step3" }, // Skip step2 for variation
                { type: "step5", selloutPrice: 120 }, // Sellout matches last channel step (step3)
              ],
            },
          ],
        },
      ],
    }),
    [],
  );

  // Memoize activeData to prevent unnecessary recalculations
  const activeData = useMemo(() => {
    console.log("� activeData evaluation:", {
      hasRealData: !!(realData && realData.topologySubsections),
      hasDataProp: !!(data && data.topologySubsections),
      scenarioId,
      isLoading,
      realDataStructure: realData ? Object.keys(realData) : null,
      realDataSubsections: realData?.topologySubsections?.length || 0,
    });

    // CRITICAL: If loading OR if we have a scenario but no real data yet, use skeleton
    if (isLoading || (scenarioId && !realData)) {
      console.log(
        "🎯 Loading state or waiting for data - forcing skeleton data",
      );
      return skeletonMockData;
    }

    // If we have real data from API, use it
    if (realData && realData.topologySubsections) {
      console.log("🎯 Using real data from API");
      return realData;
    }

    // If we have data prop, use it
    if (data && data.topologySubsections) {
      console.log("🎯 Using data prop");
      return data;
    }

    // If no scenario selected, use skeleton mock data
    if (!scenarioId) {
      console.log("🎯 No scenario - using skeleton");
      return skeletonMockData;
    }

    // Fallback to skeleton
    console.log("🎯 Fallback - using skeleton");
    return skeletonMockData;
  }, [realData, data, skeletonMockData, scenarioId, isLoading]);

  // Memoize shouldShowSkeleton to prevent unnecessary recalculations
  const shouldShowSkeleton = useMemo(() => {
    // Show skeleton when: no scenario selected OR explicitly requested OR loading OR waiting for data
    const showSkeleton =
      !scenarioId || isSkeletonMode || isLoading || (scenarioId && !realData);
    console.log("🎯 shouldShowSkeleton:", showSkeleton, {
      noScenario: !scenarioId,
      isSkeletonMode,
      isLoading,
      waitingForData: scenarioId && !realData,
      error: !!error,
      hasRealData: !!realData,
      hasDataProp: !!data,
    });
    return showSkeleton;
  }, [scenarioId, isSkeletonMode, isLoading, realData]);

  // Memoize available anchors extraction to prevent infinite loops
  const availableAnchors = useMemo(() => {
    return extractAvailableAnchors(activeData);
  }, [activeData]);

  // Set default anchor when anchors are available (skip in skeleton mode)
  useEffect(() => {
    if (!shouldShowSkeleton && availableAnchors.length > 0) {
      // If no anchor is selected, select the first one
      if (!selectedAnchor) {
        setSelectedAnchor(availableAnchors[0].id);
      }
      // If the currently selected anchor is no longer available, select the first one
      else if (
        !availableAnchors.find((anchor) => anchor.id === selectedAnchor)
      ) {
        setSelectedAnchor(availableAnchors[0].id);
      }
    } else if (shouldShowSkeleton) {
      // Clear selected anchor in skeleton mode to prevent stale state
      setSelectedAnchor("");
    }
  }, [availableAnchors, selectedAnchor, shouldShowSkeleton]);

  // Process data with relativity calculations (skip in skeleton mode)
  useEffect(() => {
    if (
      !shouldShowSkeleton &&
      activeData &&
      selectedAnchor &&
      availableAnchors.length > 0
    ) {
      // Double-check that the selected anchor is actually available in the current data
      const isValidAnchor = availableAnchors.some(
        (anchor) => anchor.id === selectedAnchor,
      );
      if (isValidAnchor) {
        const dataWithRelativity = updateChainDataWithRelativity(
          activeData,
          selectedAnchor,
        );
        setProcessedData(dataWithRelativity);
      } else {
        // If anchor is not valid, just use the raw data
        setProcessedData(activeData);
      }
    } else {
      setProcessedData(activeData);
    }
  }, [activeData, selectedAnchor, shouldShowSkeleton, availableAnchors]);

  // Memoize configuration generation to prevent unnecessary recalculations
  const config = useMemo(() => {
    try {
      const generatedConfig = assembleChainVisualizationConfig(
        processedData || activeData,
        shouldShowSkeleton ? "#f5f5f5" : clientColor,
        activeData?.gridConfig, // Pass any override options
      );

      // Override colors for skeleton mode - use lighter gray palette to match map component
      if (shouldShowSkeleton) {
        generatedConfig.visual.colors = Array(
          generatedConfig.visual.colors.length,
        ).fill("#f5f5f5");
        generatedConfig.userImplementationColor = "#f5f5f5";
      }

      return generatedConfig;
    } catch (error) {
      console.error("ChainVisualization configuration error:", error);
      return null;
    }
  }, [processedData, activeData, shouldShowSkeleton, clientColor]);

  // CRITICAL: Handle loading state AFTER all hooks - to comply with Rules of Hooks
  // Show loading skeleton when: loading OR (has scenario but no real data yet)
  if (scenarioId && (isLoading || !realData)) {
    console.log(
      "🎯 Rendering loading state immediately - isLoading:",
      isLoading,
      "hasRealData:",
      !!realData,
    );

    // Create loading skeleton with forced skeleton colors from the start
    const loadingConfig = assembleChainVisualizationConfig(
      skeletonMockData,
      "#f5f5f5", // Force skeleton color immediately
      skeletonMockData.gridConfig,
    );

    // Ensure all colors are skeleton colors to prevent any color blinking
    loadingConfig.visual.colors = Array(
      loadingConfig.visual.colors.length,
    ).fill("#f5f5f5");
    loadingConfig.userImplementationColor = "#f5f5f5";

    const loadingGridConfig = {
      ...loadingConfig.grid,
      data: loadingConfig.data,
      colors: loadingConfig.visual.colors,
      userImplementationColor: "#f5f5f5", // Force skeleton color
      helpers: loadingConfig.helpers,
      maxBoxesPerStep: loadingConfig.metrics.maxStepsInAnyTrack,
      rowPositions: loadingConfig.grid.rowConfigurations,
      gridTemplate: loadingConfig.grid.template,
      groupLabels: loadingConfig.grid.groupLabels, // Add group labels for loading state
      isSkeletonMode: true, // Ensure skeleton mode is active
      displayModes: displayModes,
    };

    const loadingContainerStyle = {
      // Add immediate skeleton styling to prevent any color flash
      backgroundColor: "#fafafa",
      transition: "none", // Disable transitions during loading
    };

    const skeletonVisualizationStyle = {
      opacity: 0.4,
      filter: "grayscale(100%)",
      transition: "none", // Disable transitions to prevent color flash
    };

    return (
      <div
        className={`${classes.container} chain-visualization-container skeleton-mode`}
        style={loadingContainerStyle}
      >
        {/* Content Area */}
        <div className={classes.contentArea} style={skeletonVisualizationStyle}>
          {/* Background Layer - Fixed (sections + price list) */}
          <div className={classes.backgroundLayer}>
            <div className={classes.backgroundLayerContent}>
              <BackgroundGrid config={loadingGridConfig} controlProps={{}} />
            </div>
          </div>

          {/* Scrollable Overlay - Scrolls over background (starts below 40px header) */}
          <div
            className={classes.scrollableOverlay}
            style={{ top: "50px", height: "calc(100% - 50px)" }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                minHeight: "100%",
                padding: "12px 0",
              }}
            >
              {/* Arrow tracks */}
              <div
                style={{
                  position: "absolute",
                  top: "12px",
                  left: 0,
                  width: "100%",
                  height: "calc(100% - 24px)",
                  pointerEvents: "none",
                }}
              >
                <ArrowGrid config={loadingGridConfig} />
              </div>

              {/* Content (Steps, labels, etc.) */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  minHeight: "calc(100% - 24px)",
                }}
              >
                <ContentGrid config={loadingGridConfig} />
              </div>
            </div>
          </div>
        </div>

        {/* ThreeDots loading indicator overlay - outside grayscale filter to preserve color */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color={clientColor}
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        </div>
      </div>
    );
  }

  // Handle configuration error
  if (!config) {
    return (
      <div className={classes.container}>
        <div style={{ textAlign: "center", padding: "20px", color: "#f44336" }}>
          Error configuring chain visualization
        </div>
      </div>
    );
  }

  // Extract configuration for backward compatibility with existing components
  const gridConfig = {
    ...config.grid,
    data: config.data,
    visual: config.visual, // Add visual object with scrollableContentHeight
    colors: config.visual.colors,
    userImplementationColor: config.userImplementationColor,
    helpers: config.helpers,
    // Legacy compatibility properties
    maxBoxesPerStep: config.metrics.maxStepsInAnyTrack,
    rowPositions: config.grid.rowConfigurations,
    gridTemplate: config.grid.template,
    groupLabels: config.grid.groupLabels, // Add group labels from grid configuration
    // Pass skeleton mode to grid components
    isSkeletonMode: shouldShowSkeleton,
    // Pass display modes array to control what values are shown
    displayModes: displayModes,
    sellinDisplayModes: sellinDisplayModes,
    showPriceList: showPriceList,
    // Pass selected anchor for bold relativity text
    selectedAnchor: selectedAnchor,
  };

  // Debug logging for group labels
  console.log(
    "🏷️ Main Component - config.grid.groupLabels:",
    config.grid.groupLabels,
  );
  console.log("🏷️ Main Component - shouldShowSkeleton:", shouldShowSkeleton);
  console.log(
    "🏷️ Main Component - activeData subsections:",
    activeData?.topologySubsections?.map((s) => s.name),
  );

  return (
    <div
      className={`${classes.container} chain-visualization-container ${shouldShowSkeleton ? "skeleton-mode" : ""}`}
      style={{
        ...(shouldShowSkeleton && {
          opacity: 0.4,
          filter: "grayscale(100%)",
        }),
      }}
    >
      {/* Content Area - Controls moved to header, BackgroundGrid handles inline controls */}
      <div className={classes.contentArea}>
        {/* Background Layer - Fixed (sections + price list + header controls) */}
        <div className={classes.backgroundLayer}>
          <div className={classes.backgroundLayerContent}>
            <BackgroundGrid
              config={gridConfig}
              controlProps={{
                availableAnchors,
                selectedAnchor,
                onAnchorChange: setSelectedAnchor,
                displayModes,
                onDisplayModeChange: setDisplayModes,
                sellinDisplayModes,
                onSellinDisplayModeChange: setSellinDisplayModes,
                showPriceList,
                onShowPriceListChange: setShowPriceList,
                clientColor,
              }}
            />
          </div>
        </div>

        {/* Scrollable Overlay - Scrolls over background (starts below 50px header) */}
        <div
          className={classes.scrollableOverlay}
          style={{ top: "50px", height: "calc(100% - 50px)" }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              minHeight: "100%",
              padding: "12px 0",
            }}
          >
            {/* Arrow tracks */}
            <div
              style={{
                position: "absolute",
                top: "12px",
                left: 0,
                width: "100%",
                height: "calc(100% - 24px)",
                pointerEvents: "none",
              }}
            >
              <ArrowGrid config={gridConfig} />
            </div>

            {/* Content (Steps, labels, etc.) */}
            <div
              style={{
                position: "relative",
                width: "100%",
                minHeight: "calc(100% - 24px)",
              }}
            >
              <ContentGrid config={gridConfig} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ChainVisualization.propTypes = {
  data: PropTypes.shape({
    priceList: PropTypes.number.isRequired,
    sections: PropTypes.arrayOf(PropTypes.string),
    gridConfig: PropTypes.shape({
      maxBoxesPerStep: PropTypes.number, // Maximum boxes that will be stacked vertically
      baseRowHeight: PropTypes.number, // Base height for single box rows
      gapBetweenSubsections: PropTypes.number, // Gap in pixels between topology subsections
    }),
    topologySubsections: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        tracks: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            steps: PropTypes.arrayOf(
              PropTypes.shape({
                type: PropTypes.string.isRequired,
                node2_reference_client: PropTypes.array, // Array of data sets for stacked boxes
                node3_reference_pos: PropTypes.array, // Array of data sets for stacked boxes
                tooltipDetails: PropTypes.array, // Array of detail records for tooltip
                // ... other step properties
              }),
            ).isRequired,
          }),
        ).isRequired,
      }),
    ).isRequired,
  }),
  isSkeletonMode: PropTypes.bool,
  scenarioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filters: PropTypes.object,
  selectedVolume: PropTypes.string, // Direct backend value: "volume_sellthrough", "volume_sellin", or "1"
  selectedRoutes: PropTypes.string, // Routes option from parent (controlled by BoardTab ScenarioBar)
};

export default ChainVisualization;
